import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

import { applyLook } from '../../lib/lookStore';
import { setTema, useTema } from '../../lib/tema';
import { statusOf } from '../lib/payment-labels';
import { useAdminSession } from '../useAdminSession';
import { HqIcon } from './HqIcon';
import { HqMonthSwitch } from './HqMonthSwitch';
import { HqSearch } from './HqSearch';
import { activeSection, backPlace, HQ_MORE, HQ_PRIMARY, HQ_SECONDARY, HQ_TABS, initialsOf, type HqPlace } from './nav';
import { usePaymentsQuery } from './queries';

/**
 * Dale HQ keeps the look of Dale (cream paper, copper, Fraunces and Instrument
 * Sans) whichever look the device picked for the CRM: the owner's panel is not
 * one of the looks. The device's own look comes back when HQ closes.
 */
function usePinnedDaleLook() {
  useEffect(() => {
    applyLook('dale');
    return () => applyLook();
  }, []);
}

/** How many payments an owner reported that a person still has to confirm. */
function useWaitingCount(): number {
  const payments = usePaymentsQuery().data?.data ?? [];
  return payments.filter((payment) => statusOf(payment) === 'reportado').length;
}

function Badge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="hq-badge" aria-label={`${count} por confirmar`}>
      {count}
    </span>
  );
}

function PlaceLink({ place, active, waiting, onClick }: { place: HqPlace; active: boolean; waiting: number; onClick?: () => void }) {
  return (
    <Link to={place.to} className="hq-nav-item" data-active={active || undefined} aria-current={active ? 'page' : undefined} onClick={onClick}>
      <HqIcon name={place.icon} />
      <span className="hq-nav-label">{place.label}</span>
      {place.id === 'pagos' ? <Badge count={waiting} /> : null}
    </Link>
  );
}

type HqShellProps = { children: ReactNode };

/**
 * The frame of every place of Dale HQ: the sidebar on a desktop, the tab bar on a
 * phone (both are always in the page; the stylesheet shows the one that fits),
 * and the top bar with the month, the client search and who is signed in.
 */
export function HqShell({ children }: HqShellProps) {
  usePinnedDaleLook();
  const { session, logout } = useAdminSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tema = useTema();
  const waiting = useWaitingCount();
  const [moreOpen, setMoreOpen] = useState(false);
  const section = activeSection(pathname);
  const back = backPlace(pathname);
  const username = session?.username ?? '';

  // A place was chosen: the sheet has done its job.
  useEffect(() => setMoreOpen(false), [pathname]);

  async function signOut() {
    await logout();
    void navigate({ to: '/admin/entrar', replace: true });
  }

  const night = tema === 'noche';

  return (
    <div className="hq-app" data-section={section ?? undefined}>
      <aside className="hq-sidebar">
        <Link to="/admin" className="hq-brand" aria-label="Dale HQ, inicio">
          <span className="hq-brand-word">
            Dale<span className="hq-brand-dot">.</span>
          </span>
          <span className="hq-brand-sub">HQ</span>
        </Link>
        <nav className="hq-nav" aria-label="Secciones de Dale HQ">
          <ul>
            {HQ_PRIMARY.map((place) => (
              <li key={place.id}>
                <PlaceLink place={place} active={section === place.id} waiting={waiting} />
              </li>
            ))}
          </ul>
          <ul className="hq-nav-more">
            {HQ_SECONDARY.map((place) => (
              <li key={place.id}>
                <PlaceLink place={place} active={section === place.id} waiting={waiting} />
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="hq-main">
        <header className="hq-topbar">
          <span className="hq-phone-brand" aria-hidden="true">
            <span className="hq-brand-word">
              Dale<span className="hq-brand-dot">.</span>
            </span>
            <span className="hq-brand-sub">HQ</span>
          </span>
          {back ? (
            <button type="button" className="hq-back" onClick={() => void navigate({ to: back })}>
              <HqIcon name="izquierda" size={18} />
              Volver
            </button>
          ) : null}
          <span className="hq-tenant">
            <HqIcon name="organizaciones" size={18} />
            Dale HQ
          </span>
          <HqMonthSwitch />
          <span className="hq-space" />
          <HqSearch />
          <button
            type="button"
            className="hq-iconbtn"
            aria-label={night ? 'Cambiar a modo día' : 'Cambiar a modo noche'}
            onClick={() => setTema(night ? 'dia' : 'noche')}
          >
            <HqIcon name={night ? 'sol' : 'luna'} size={20} />
          </button>
          <span className="hq-user">
            <span className="hq-avatar" aria-hidden="true">
              {initialsOf(username).slice(0, 1)}
            </span>
            <span className="hq-user-text">
              <strong>{username}</strong>
              <small>Administrador</small>
            </span>
          </span>
          <button type="button" className="hq-signout" onClick={() => void signOut()}>
            Salir
          </button>
        </header>
        <div className="hq-content">{children}</div>
      </div>

      <nav className="hq-tabbar" aria-label="Pestañas de Dale HQ">
        {HQ_TABS.map((tab) =>
          tab.to ? (
            <Link key={tab.id} to={tab.to} className="hq-tab" data-active={section === tab.id || undefined} aria-current={section === tab.id ? 'page' : undefined}>
              <HqIcon name={tab.icon} size={24} />
              <span>{tab.label}</span>
            </Link>
          ) : (
            <button
              key={tab.id}
              type="button"
              className="hq-tab"
              data-active={HQ_MORE.some((place) => place.id === section) || undefined}
              aria-current={HQ_MORE.some((place) => place.id === section) ? 'page' : undefined}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              onClick={() => setMoreOpen((open) => !open)}
            >
              <HqIcon name={tab.icon} size={24} />
              <span>{tab.label}</span>
            </button>
          ),
        )}
      </nav>

      {moreOpen ? (
        <div className="hq-more">
          <button type="button" className="hq-more-scrim" aria-label="Cerrar" onClick={() => setMoreOpen(false)} />
          <div className="hq-more-sheet" role="dialog" aria-label="Más de Dale HQ">
            <ul>
              {HQ_MORE.map((place) => (
                <li key={place.id}>
                  <PlaceLink place={place} active={section === place.id} waiting={waiting} onClick={() => setMoreOpen(false)} />
                </li>
              ))}
            </ul>
            <button type="button" className="hq-nav-item hq-more-signout" onClick={() => void signOut()}>
              <HqIcon name="salir" />
              <span className="hq-nav-label">Salir</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
